import type { ReactNode } from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { cn } from '../cn.js';

export type AlertTone = 'info' | 'success' | 'warning' | 'danger';

const TONES: Record<AlertTone, { classes: string; Icon: typeof InformationCircleIcon }> = {
  info: { classes: 'bg-info-bg text-info-fg border-info-border', Icon: InformationCircleIcon },
  success: {
    classes: 'bg-success-bg text-success-fg border-success-border',
    Icon: CheckCircleIcon,
  },
  warning: {
    classes: 'bg-warning-bg text-warning-fg border-warning-border',
    Icon: ExclamationTriangleIcon,
  },
  danger: { classes: 'bg-danger-bg text-danger-fg border-danger-border', Icon: XCircleIcon },
};

export interface AlertProps {
  tone?: AlertTone;
  title?: ReactNode;
  children: ReactNode;
  actions?: ReactNode;
  icon?: ReactNode;
  className?: string;
}

export function Alert({ tone = 'info', title, children, actions, icon, className }: AlertProps) {
  const { classes, Icon } = TONES[tone];

  return (
    <div
      // role=alert is assertive and interrupts a screen reader. Reserve it for
      // things the user must act on; informational notices use role=status.
      role={tone === 'danger' || tone === 'warning' ? 'alert' : 'status'}
      className={cn(
        'flex items-start gap-2.5 rounded-lg border px-4 py-3 text-sm',
        classes,
        className,
      )}
    >
      {icon ?? <Icon className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />}
      <div className="min-w-0 flex-1">
        {title ? <div className="font-semibold">{title}</div> : null}
        <div className={title ? 'mt-0.5' : undefined}>{children}</div>
      </div>
      {actions ? <div className="shrink-0 flex items-center gap-2">{actions}</div> : null}
    </div>
  );
}
